import jsPDF from "jspdf";

/**
 * Generate a multi-page A4 PDF from an iDiag diagnosis payload.
 * Text-based rendering (not html2canvas) so the PDF is selectable & compact.
 */
export function exportDiagnosisToPdf({ data, t, i18n }) {
  const diag = data?.diagnosis || {};
  const created = data?.created_at || new Date().toISOString();
  const lang = i18n?.resolvedLanguage || "es";

  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 44;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const colors = {
    bg: [10, 10, 15],
    primary: [26, 107, 255],
    text: [28, 28, 32],
    mute: [110, 114, 125],
    rule: [220, 222, 228],
    severity: {
      low: [34, 160, 91],
      medium: [217, 156, 35],
      high: [232, 118, 28],
      critical: [224, 58, 48],
    },
  };

  function ensureSpace(needed) {
    if (y + needed > pageHeight - margin - 18) {
      addFooter();
      pdf.addPage();
      y = margin;
      addHeader();
    }
  }

  function addHeader() {
    pdf.setFillColor(...colors.bg);
    pdf.rect(0, 0, pageWidth, 36, "F");
    pdf.setFontSize(13);
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.text("iDiag", margin, 24);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(170, 175, 190);
    pdf.text(
      t("app.tagline", "iPhone Diagnostic"),
      margin + 46,
      24,
    );
    const dateLabel = `${t("pdf.generatedOn")} ${new Date(created).toLocaleString(lang)}`;
    pdf.text(dateLabel, pageWidth - margin, 24, { align: "right" });
    y = Math.max(y, 56);
  }

  function addFooter() {
    const pageCount = pdf.internal.getNumberOfPages();
    const currentPage = pdf.internal.getCurrentPageInfo().pageNumber;
    pdf.setFontSize(8);
    pdf.setTextColor(...colors.mute);
    pdf.text(
      `${t("pdf.generatedBy")} · ${t("pdf.page")} ${currentPage}/${pageCount}`,
      pageWidth / 2,
      pageHeight - 18,
      { align: "center" },
    );
  }

  function h1(text) {
    ensureSpace(30);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.setTextColor(...colors.text);
    pdf.text(text, margin, y);
    y += 22;
  }

  function h2(text) {
    ensureSpace(24);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.setTextColor(...colors.primary);
    pdf.text(text.toUpperCase(), margin, y);
    y += 6;
    pdf.setDrawColor(...colors.rule);
    pdf.setLineWidth(0.5);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 14;
  }

  function kv(label, value) {
    if (value == null || value === "") return;
    ensureSpace(18);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(...colors.mute);
    pdf.text(String(label).toUpperCase(), margin, y);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.setTextColor(...colors.text);
    const lines = pdf.splitTextToSize(String(value), contentWidth);
    pdf.text(lines, margin, y + 13);
    y += 13 + lines.length * 14 + 6;
  }

  function paragraph(text) {
    if (!text) return;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.setTextColor(...colors.text);
    const paras = String(text).split(/\n\n+/);
    for (const p of paras) {
      const lines = pdf.splitTextToSize(p.trim(), contentWidth);
      ensureSpace(lines.length * 14 + 6);
      pdf.text(lines, margin, y);
      y += lines.length * 14 + 8;
    }
  }

  function bullets(items) {
    if (!items || !items.length) return;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.setTextColor(...colors.text);
    for (const it of items) {
      const lines = pdf.splitTextToSize(`•  ${it}`, contentWidth - 8);
      ensureSpace(lines.length * 14 + 4);
      pdf.text(lines, margin + 4, y);
      y += lines.length * 14 + 2;
    }
    y += 4;
  }

  function orderedList(items) {
    if (!items || !items.length) return;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.setTextColor(...colors.text);
    items.forEach((it, i) => {
      const lines = pdf.splitTextToSize(`${i + 1}.  ${it}`, contentWidth - 8);
      ensureSpace(lines.length * 14 + 4);
      pdf.text(lines, margin + 4, y);
      y += lines.length * 14 + 2;
    });
    y += 4;
  }

  function severityBadge(severity) {
    const color = colors.severity[severity] || colors.severity.medium;
    const label = `${t("result.severity")}: ${t("result.severity" + (severity ? severity.charAt(0).toUpperCase() + severity.slice(1) : "Medium"))}`;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    const w = pdf.getTextWidth(label) + 16;
    const x = pageWidth - margin - w;
    pdf.setFillColor(...color);
    pdf.roundedRect(x, y - 12, w, 20, 6, 6, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.text(label, x + 8, y + 2);
  }

  // -------- Build PDF --------
  addHeader();

  // Top block: primary faulty component
  h1(diag.primary_faulty_component || diag.faulty_component || t("result.title"));
  severityBadge(diag.severity);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(11);
  pdf.setTextColor(...colors.mute);
  const tagLine = [diag.panic_tag || diag.panic_type, diag.panic_type !== diag.panic_tag ? diag.panic_type : null]
    .filter(Boolean)
    .join("   ·   ");
  if (tagLine) {
    pdf.text(tagLine, margin, y + 4);
    y += 20;
  }

  // Meta grid
  h2(t("result.device"));
  kv(t("result.device"), diag.device_model);
  kv(t("result.cpu"), diag.cpu);
  kv(t("result.ios"), diag.ios_version);
  kv(t("result.confidence"), `${diag.confidence ?? 0}%`);
  kv(t("result.savedAt"), new Date(created).toLocaleString(lang));

  if (diag.short_description) {
    h2(t("result.shortDescription"));
    paragraph(diag.short_description);
  }

  if ((diag.involved_components || []).length) {
    h2(t("result.involved"));
    bullets(diag.involved_components);
  }

  if (diag.technical_analysis) {
    h2(t("result.technicalAnalysis"));
    paragraph(diag.technical_analysis);
  }

  if ((diag.repair_steps || []).length) {
    h2(t("result.repairSteps"));
    for (const s of diag.repair_steps) {
      ensureSpace(28);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(12);
      pdf.setTextColor(...colors.text);
      pdf.text(`${s.order}. ${s.title || ""}`, margin, y);
      y += 16;
      if (s.objective) {
        pdf.setFont("helvetica", "italic");
        pdf.setFontSize(10);
        pdf.setTextColor(...colors.mute);
        const ob = pdf.splitTextToSize(`${t("result.stepObjective")}: ${s.objective}`, contentWidth);
        ensureSpace(ob.length * 14 + 4);
        pdf.text(ob, margin, y);
        y += ob.length * 14 + 4;
      }
      if ((s.common_faults || []).length) {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        pdf.setTextColor(...colors.text);
        ensureSpace(14);
        pdf.text(`${t("result.stepCommonFaults")}:`, margin, y);
        y += 12;
        bullets(s.common_faults);
      }
      if ((s.procedures || []).length) {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        pdf.setTextColor(...colors.text);
        ensureSpace(14);
        pdf.text(`${t("result.stepProcedures")}:`, margin, y);
        y += 12;
        bullets(s.procedures);
      }
      if ((s.expected_results || []).length) {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        pdf.setTextColor(...colors.text);
        ensureSpace(14);
        pdf.text(`${t("result.stepExpected")}:`, margin, y);
        y += 12;
        bullets(s.expected_results);
      }
      y += 6;
    }
  }

  if ((diag.replacement_order || []).length) {
    h2(t("result.replacementOrder"));
    orderedList(diag.replacement_order);
  }

  if ((diag.post_repair_notes || []).length) {
    h2(t("result.postRepair"));
    bullets(diag.post_repair_notes);
  }

  if (diag.conclusion) {
    h2(t("result.conclusion"));
    paragraph(diag.conclusion);
  }

  addFooter();

  const slug = (s, fallback = "diagnostic") => {
    const text = String(s || "").trim();
    if (!text) return fallback;
    const normalized = text
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // strip accents
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    return normalized || fallback;
  };
  const deviceSlug = slug(diag.device_model, "iphone");
  const componentSlug = slug(
    diag.primary_faulty_component || diag.faulty_component,
    "diagnostic",
  );
  const dateSlug = new Date(created).toISOString().slice(0, 10);
  const fileName = `idiag-${deviceSlug}-${componentSlug}-${dateSlug}.pdf`;
  pdf.save(fileName);
}
